import { Component, ChangeDetectionStrategy, model } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';

export interface PipelineStageInfo {
  status: string;
  pillLabel: string;
  badgeClass: string;
  meaning: string;
  whoControlsIt: string;
}

@Component({
  selector: 'rapider-task-pipeline-guide-modal',
  standalone: true,
  imports: [
    CommonModule,
    NzModalModule,
    NzButtonModule,
    NzTagModule,
    NzTooltipModule,
  ],
  templateUrl: './task-pipeline-guide-modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TaskPipelineGuideModalComponent {
  /** Two-way bindable visibility model */
  public readonly isVisible = model<boolean>(false);

  public readonly stages: PipelineStageInfo[] = [
    {
      status: 'Open',
      pillLabel: 'Open',
      badgeClass: 'bg-gray-100 text-gray-700 border-gray-200',
      meaning: 'Newly created task, awaiting acceptance',
      whoControlsIt: 'Reporter / Admin',
    },
    {
      status: 'Accepted',
      pillLabel: 'Accepted',
      badgeClass: 'bg-blue-50 text-blue-700 border-blue-200',
      meaning: 'Admin approves the task for the AI Builder queue',
      whoControlsIt: 'Admin / Owner',
    },
    {
      status: 'In Progress',
      pillLabel: 'In Progress',
      badgeClass: 'bg-blue-100 text-blue-800 border-blue-200',
      meaning: 'AI Builder or developer is working',
      whoControlsIt: 'AI / Developer',
    },
    {
      status: 'In Review',
      pillLabel: 'In Review',
      badgeClass: 'bg-purple-100 text-purple-800 border-purple-200',
      meaning: 'Development complete; awaiting QA verification',
      whoControlsIt: 'QA',
    },
    {
      status: 'Ready for Release',
      pillLabel: 'Ready for Release',
      badgeClass: 'bg-orange-100 text-orange-800 border-orange-200',
      meaning: 'QA passed; awaiting deployment',
      whoControlsIt: 'QA / System',
    },
    {
      status: 'Released',
      pillLabel: 'Released',
      badgeClass: 'bg-sky-100 text-sky-800 border-sky-200',
      meaning: 'Fix deployed; awaiting reporter confirmation',
      whoControlsIt: 'System',
    },
    {
      status: 'Confirmed',
      pillLabel: 'Confirmed',
      badgeClass: 'bg-emerald-100 text-emerald-800 border-emerald-200',
      meaning: 'Original reporter confirms the fix works',
      whoControlsIt: 'Reporter',
    },
    {
      status: 'Reopened',
      pillLabel: 'Reopened',
      badgeClass: 'bg-rose-100 text-rose-800 border-rose-200',
      meaning: 'Reporter says the issue persists; reason required',
      whoControlsIt: 'Reporter',
    },
    {
      status: 'Closed',
      pillLabel: 'Closed',
      badgeClass: 'bg-slate-100 text-slate-700 border-slate-200',
      meaning: 'Admin terminates the task without completing it',
      whoControlsIt: 'Admin / Owner',
    },
  ];

  public close(): void {
    this.isVisible.set(false);
  }
}
